import { Link } from 'react-router-dom';
import { ArrowRight, Eye, Brush, Monitor, FileOutput, Layers, Clapperboard } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { SEO } from '@/components/SEO';

const capabilities = [
  { icon: Brush, title: 'Color Grading', desc: 'Cinematic color work that defines visual identity. DaVinci Resolve with calibrated reference monitoring.' },
  { icon: Monitor, title: 'Online & Finishing', desc: 'Conform, finishing, and QC to meet broadcast and theatrical standards.' },
  { icon: FileOutput, title: 'Delivery', desc: 'Mastering in all required formats — DCP, streaming, broadcast, social, archival.' },
  { icon: Layers, title: 'Visual Continuity', desc: 'Consistent vision across every frame — from dailies to final grade.' },
  { icon: Eye, title: 'Look Development', desc: 'Collaborative look creation during pre-production and on-set.' },
  { icon: Clapperboard, title: 'Workflow Design', desc: 'Custom post workflows for your project\'s specific needs and timeline.' },
];

export default function PostPage() {
  return (
    <div>
      <SEO 
        title="Post-Production & Color Grading | Baklight Films Mexico"
        description="Professional color grading, finishing, and delivery services. DaVinci Resolve workflows for documentary, narrative, and commercial projects."
        path="/post"
      />
      <PageHeader
        meta="Post & Finishing"
        title="Cinematic finishing. Not just process — craft."
        subtitle="Baklight's post-production division treats finishing as an extension of the creative vision. Color, conform, and delivery with the same intentionality as the shoot itself."
      />

      {/* Capabilities */}
      <section className="pb-14 sm:pb-20">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((item, i) => (
              <SectionReveal key={item.title} delay={i * 0.08}>
                <div className="p-6 rounded-lg border border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg-elev))] h-full">
                  <item.icon size={24} className="text-[hsl(var(--bk-accent))] mb-4" strokeWidth={1.5} />
                  <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-2">{item.title}</h3>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{item.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-14 sm:py-20 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Our Approach</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-10">
              From first frame to final delivery.
            </h2>
          </SectionReveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Look Development', desc: 'Collaborative look creation during pre-production.' },
              { step: '02', title: 'On-Set Support', desc: 'Live grading and DIT to ensure visual consistency.' },
              { step: '03', title: 'Color & Finishing', desc: 'Full cinematic grade and conform in calibrated suite.' },
              { step: '04', title: 'Mastering & Delivery', desc: 'Final mastering in all required formats with QC.' },
            ].map((item, i) => (
              <SectionReveal key={item.step} delay={i * 0.1}>
                <div className="p-6 rounded-lg border border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg))]">
                  <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-3">{item.step}</p>
                  <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-2">{item.title}</h3>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{item.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-14 sm:py-20">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <div className="aspect-[21/9] rounded-lg overflow-hidden">
              <img
                src="/assets/post-room.webp"
                alt="Baklight Post-Production Suite — Professional color grading and finishing room"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 text-center">
          <SectionReveal>
            <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] text-[hsl(var(--bk-fg))]">
              Need a finishing partner?
            </h2>
            <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mt-4 max-w-[500px] mx-auto">
              Whether you need a full grade or a conform-to-delivery pipeline, we're here.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 font-body text-sm px-8 py-3 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity active:scale-[0.98] mt-8"
            >
              Discuss Post-Production <ArrowRight size={16} />
            </Link>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
